(() => {
  'use strict';
  const root = typeof window === 'undefined' ? globalThis : window;
  if (root.JoffzDeliveryEngine) {
    if (typeof module !== 'undefined') module.exports = root.JoffzDeliveryEngine;
    return;
  }
  const DAY = 86400000;
  const iso = (date) => date.toISOString().slice(0, 10);
  const dateFromISO = (value) => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new TypeError('Invalid calendar date');
    const result = new Date(`${value}T12:00:00Z`);
    if (!Number.isFinite(result.getTime()) || iso(result) !== value) throw new TypeError('Invalid calendar date');
    return result;
  };
  const integer = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;
  const range = (value) => {
    if (!value || !integer(value.min, 0, 90) || !integer(value.max, value.min, 90)) throw new TypeError('Invalid business-day range');
    return { min: value.min, max: value.max };
  };
  const calendar = (value = {}) => {
    const days = value.weekdays ?? [1, 2, 3, 4, 5];
    const holidays = value.holidays ?? [];
    const blackouts = value.blackouts ?? [];
    if (!Array.isArray(days) || !days.length || days.length > 7 || new Set(days).size !== days.length || days.some((d) => !integer(d, 0, 6))) throw new TypeError('Invalid working week');
    if (!Array.isArray(holidays) || !Array.isArray(blackouts) || holidays.length > 366 || blackouts.length > 366) throw new TypeError('Invalid calendar');
    if ((holidays.length || blackouts.length || days.join(',') !== '1,2,3,4,5') && value.verified !== true) throw new TypeError('Unverified calendar');
    const closed = new Set([...holidays, ...blackouts].map((day) => iso(dateFromISO(day))));
    return { days: new Set(days), closed };
  };
  const isBusinessDay = (date, schedule) => schedule.days.has(date.getUTCDay()) && !schedule.closed.has(iso(date));
  const addBusinessDays = (start, count, scheduleInput = {}) => {
    if (!integer(count, 0, 180)) throw new TypeError('Invalid business-day count');
    const schedule = calendar(scheduleInput);
    let date = dateFromISO(start);
    let remaining = count;
    let steps = 0;
    while (remaining > 0 && steps < 1460) {
      date = new Date(date.getTime() + DAY);
      if (date.getUTCFullYear() > 9999) throw new RangeError('Calendar range exceeded');
      if (isBusinessDay(date, schedule)) remaining -= 1;
      steps += 1;
    }
    if (remaining) throw new RangeError('No valid delivery date');
    return iso(date);
  };
  const localClock = (instant, timeZone) => {
    if (!(instant instanceof Date) || !Number.isFinite(instant.getTime())) throw new TypeError('Invalid order date');
    const formatter = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
    const fields = Object.fromEntries(formatter.formatToParts(instant).map((part) => [part.type, part.value]));
    const day = `${fields.year}-${fields.month}-${fields.day}`;
    dateFromISO(day);
    return { day, minute: Number(fields.hour) * 60 + Number(fields.minute), second: Number(fields.second) };
  };
  const calculate = ({ now = new Date(), country, personalized = false, available = true, config } = {}) => {
    if (available === false) return { status: 'fallback', reason: 'unavailable' };
    if (!config || config.enabled === false) return { status: 'fallback', reason: 'disabled' };
    if (config.policyVerified !== true) return { status: 'fallback', reason: 'unverified_policy' };
    if (typeof country !== 'string' || !/^[A-Z]{2}$/.test(country)) return { status: 'fallback', reason: 'unknown_destination' };
    if (personalized && (!config.personalizedPreparation || config.personalizedPreparation.verified !== true)) return { status: 'fallback', reason: 'personalized' };
    try {
      if (typeof config.timeZone !== 'string' || !config.timeZone) throw new TypeError('Missing store timezone');
      const clock = localClock(now, config.timeZone);
      const preparation = range(personalized ? config.personalizedPreparation : config.preparation);
      const productionCalendar = config.productionCalendar ?? {};
      const production = calendar(productionCalendar);
      if (!Array.isArray(config.routes) || !config.routes.length || config.routes.length > 100) throw new TypeError('Invalid routes');
      const known = new Set();
      let selected;
      for (const route of config.routes) {
        if (!Array.isArray(route.countries) || !route.countries.length) throw new TypeError('Missing route countries');
        for (const code of route.countries) {
          if (typeof code !== 'string' || !/^[A-Z]{2}$/.test(code) || known.has(code)) throw new TypeError('Invalid or duplicate destination');
          known.add(code);
        }
        range(route.transit);
        calendar(route.calendar);
        if (route.countries.includes(country)) selected = route;
      }
      if (!selected) return { status: 'fallback', reason: 'unknown_destination' };
      let start = clock.day;
      if (config.cutoffMinute !== undefined && config.cutoffMinute !== null) {
        if (config.cutoffVerified !== true || !integer(config.cutoffMinute, 0, 1439)) throw new TypeError('Unverified or invalid cutoff');
        if (clock.minute >= config.cutoffMinute && isBusinessDay(dateFromISO(start), production)) start = addBusinessDays(start, 1, productionCalendar);
      }
      const dispatchMin = addBusinessDays(start, preparation.min, productionCalendar);
      const dispatchMax = addBusinessDays(start, preparation.max, productionCalendar);
      const arrivalMin = addBusinessDays(dispatchMin, selected.transit.min, selected.calendar);
      const arrivalMax = addBusinessDays(dispatchMax, selected.transit.max, selected.calendar);
      if (arrivalMin > arrivalMax) throw new RangeError('Reversed delivery range');
      return { status: 'estimate', orderDate: clock.day, dispatchMin, dispatchMax, arrivalMin, arrivalMax, preparation, transit: range(selected.transit), country };
    } catch (_) {
      return { status: 'fallback', reason: 'invalid_config' };
    }
  };
  const formatDate = (value, locale, country, includeYear = false) => {
    const date = dateFromISO(value);
    let language = 'en';
    try { language = new Intl.Locale(locale || 'en', country ? { region: country } : undefined).toString(); } catch (_) {}
    return new Intl.DateTimeFormat(language, { timeZone: 'UTC', day: 'numeric', month: 'short', ...(includeYear ? { year: 'numeric' } : {}) }).format(date);
  };
  const engine = Object.freeze({ calculate, addBusinessDays, localClock, formatDate });
  root.JoffzDeliveryEngine = engine;
  if (typeof module !== 'undefined') module.exports = engine;
  if (typeof customElements === 'undefined' || typeof HTMLElement === 'undefined' || customElements.get('joffz-delivery-estimator')) return;
  class JoffzDeliveryEstimator extends HTMLElement {
    static get observedAttributes() { return ['data-country', 'data-personalized', 'data-available', 'data-config', 'data-locale']; }
    connectedCallback() {
      if (this._active) return;
      this._active = true;
      this._refresh = () => { if (!document.hidden) this.refresh(); };
      window.addEventListener('pageshow', this._refresh);
      document.addEventListener('visibilitychange', this._refresh);
      this.refresh();
    }
    disconnectedCallback() {
      this._active = false;
      clearTimeout(this._timer);
      window.removeEventListener('pageshow', this._refresh);
      document.removeEventListener('visibilitychange', this._refresh);
    }
    attributeChangedCallback() { if (this._active) this.refresh(); }
    refresh() {
      clearTimeout(this._timer);
      let config;
      try { config = JSON.parse(this.dataset.config); } catch (_) {}
      const result = calculate({ config, country: this.dataset.country, personalized: this.dataset.personalized === 'true', available: this.dataset.available !== 'false' });
      const fallback = this.querySelector('[data-delivery-fallback]');
      const estimate = this.querySelector('[data-delivery-estimate]');
      this.dataset.state = result.status;
      this.dataset.reason = result.reason || '';
      if (fallback) {
        const personalized = this.dataset.personalized === 'true';
        fallback.textContent = personalized ? this.dataset.fallbackPersonalized : this.dataset.fallback;
        fallback.setAttribute('aria-hidden', String(result.status === 'estimate'));
      }
      if (estimate) estimate.setAttribute('aria-hidden', String(result.status !== 'estimate'));
      if (result.status === 'estimate') {
        const includeYear = result.arrivalMin.slice(0, 4) !== result.arrivalMax.slice(0, 4) || result.arrivalMax.slice(0, 4) !== result.orderDate.slice(0, 4);
        for (const [name, value] of [['order', result.orderDate], ['min', result.arrivalMin], ['max', result.arrivalMax]]) {
          const element = this.querySelector(`[data-delivery-date="${name}"]`);
          if (!element) continue;
          element.textContent = formatDate(value, this.dataset.locale || document.documentElement.lang, result.country, name !== 'order' && includeYear);
          element.setAttribute('datetime', value);
        }
        const detail = this.querySelector('[data-delivery-breakdown]');
        if (detail) detail.textContent = (this.dataset.details || '').replace('{prep}', `${result.preparation.min}–${result.preparation.max}`).replace('{transit}', `${result.transit.min}–${result.transit.max}`);
      }
      if (config?.enabled !== false && this.isConnected && !document.hidden) {
        let delay = 3600000;
        try {
          const clock = localClock(new Date(), config.timeZone);
          const next = [1440, config.cutoffVerified ? config.cutoffMinute : null].filter((minute) => Number.isInteger(minute) && minute > clock.minute);
          delay = Math.min(delay, (Math.min(...next) - clock.minute) * 60000 - clock.second * 1000 + 1000);
        } catch (_) {}
        this._timer = setTimeout(() => this.refresh(), delay);
      }
    }
  }
  customElements.define('joffz-delivery-estimator', JoffzDeliveryEstimator);
})();
